<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">公众号账户管理</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.keyword" placeholder="请输入公众号名称或AppID" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增公众号
        </el-button>
      </div>

      <!-- 公众号列表 -->
      <el-table :data="accountList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="account_name" label="公众号名称" width="180" />
        <el-table-column prop="appid" label="AppID" width="220" />
        <el-table-column label="统计信息" width="200">
          <template #default="{ row }">
            <div class="stats-info">
              <div>模板: {{ row.template_count }}</div>
              <div>用户: {{ row.user_count }}</div>
              <div>标签: {{ row.tag_count }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="notes" label="备注" width="150" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="操作" width="550" fixed="right">
          <template #default="scope">
            <div class="action-buttons">
              <el-button type="primary" size="small" @click="openSyncDialog(scope.row)" class="action-btn">
                <el-icon>
                  <Refresh />
                </el-icon>
                同步
              </el-button>
              <el-button type="info" size="small" @click="openTemplateDialog(scope.row)" class="action-btn">
                <el-icon>
                  <Document />
                </el-icon>
                模板
              </el-button>
              <el-button type="warning" size="small" @click="openUserDialog(scope.row)" class="action-btn">
                <el-icon>
                  <User />
                </el-icon>
                用户
              </el-button>
              <el-button type="success" size="small" @click="openSendDialog(scope.row)" class="action-btn">
                <el-icon>
                  <Message />
                </el-icon>
                发送
              </el-button>
              <el-button type="primary" size="small" @click="openEditDialog(scope.row)" class="action-btn">
                <el-icon>
                  <Edit />
                </el-icon>
                编辑
              </el-button>
              <el-button type="danger" size="small" @click="handleDelete(scope.row.id)" class="action-btn">
                <el-icon>
                  <Delete />
                </el-icon>
                删除
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchAccountList" @current-change="fetchAccountList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
      <el-form ref="accountForm" :model="accountForm" label-width="120px" :rules="formRules">
        <el-form-item label="公众号名称" prop="account_name">
          <el-input v-model="accountForm.account_name" placeholder="请输入公众号名称" size="large" />
        </el-form-item>

        <el-form-item label="AppID" prop="appid">
          <el-input v-model="accountForm.appid" placeholder="请输入公众号AppID" size="large" />
        </el-form-item>

        <el-form-item label="AppSecret" prop="appsecret">
          <el-input v-model="accountForm.appsecret" placeholder="请输入公众号AppSecret" type="password" show-password
            size="large" />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="accountForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="accountForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAccountForm">确认</el-button>
      </template>
    </el-dialog>

    <!-- 同步对话框 -->
    <el-dialog v-model="syncDialogVisible" title="同步公众号数据" width="500px" :close-on-click-modal="false">
      <el-alert type="info" show-icon style="margin-bottom: 20px;">
        同步操作将从微信服务器获取最新的模板、用户和标签数据
      </el-alert>

      <el-form label-width="120px">
        <el-form-item label="同步选项">
          <el-checkbox-group v-model="syncOptions">
            <el-checkbox label="templates">模板列表</el-checkbox>
            <el-checkbox label="users">用户列表</el-checkbox>
            <el-checkbox label="tags">标签列表</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="syncDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSync" :loading="syncing">开始同步</el-button>
      </template>
    </el-dialog>

    <!-- 模板管理对话框 -->
    <el-dialog v-model="templateDialogVisible" :title="templateDialogTitle" width="80%" top="5vh">
      <WechatTemplateView v-if="templateDialogVisible" :account-id="currentAccountId" :store-id="userInfo.store_id"
        :user-id="userInfo.id" @template-selected="handleTemplateSelectedFromDialog" />
    </el-dialog>

    <!-- 用户管理对话框 -->
    <el-dialog v-model="userDialogVisible" :title="userDialogTitle" width="90%" top="5vh">
      <WechatUserView v-if="userDialogVisible" :account-id="currentAccountId" :store-id="userInfo.store_id"
        :user-id="userInfo.id" @user-selected="handleUserSelectedFromDialog" />
    </el-dialog>

    <!-- 消息发送对话框 -->
    <el-dialog v-model="sendDialogVisible" :title="sendDialogTitle" width="95%" top="5vh">
      <WechatSendMessage v-if="sendDialogVisible" :account-id="currentAccountId" :store-id="userInfo.store_id"
        :user-id="userInfo.id" ref="sendMessageComponent" />
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Refresh, Document, User, Message
} from '@element-plus/icons-vue'
import WechatTemplateView from '@/components/WechatTemplateView.vue'
import WechatUserView from '@/components/WechatUserView.vue'
import WechatSendMessage from '@/components/WechatSendMessage.vue'

export default {
  name: 'WechatAccount',
  components: {
    Search, Plus, Edit, Delete, Refresh, Document, User, Message,
    WechatTemplateView, WechatUserView, WechatSendMessage
  },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      syncDialogVisible: false,
      templateDialogVisible: false,
      userDialogVisible: false,
      sendDialogVisible: false,
      syncing: false,
      dialogTitle: '新增公众号',
      templateDialogTitle: '',
      userDialogTitle: '',
      sendDialogTitle: '',
      isEditMode: false,
      currentAccountId: null,
      currentAccount: null,
      syncOptions: ['templates', 'users', 'tags'],
      searchForm: {
        keyword: ''
      },
      accountForm: {
        id: null,
        account_name: '',
        appid: '',
        appsecret: '',
        status: 1,
        notes: ''
      },
      formRules: {
        account_name: [
          { required: true, message: '请输入公众号名称', trigger: 'blur' }
        ],
        appid: [
          { required: true, message: '请输入AppID', trigger: 'blur' }
        ],
        appsecret: [
          { required: true, message: '请输入AppSecret', trigger: 'blur' }
        ]
      },
      accountList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('pc_user_info') || '{}')
    }
  },
  mounted() {
    this.fetchAccountList();
  },
  methods: {
    // 获取公众号列表
    async fetchAccountList() {
      this.loading = true;
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.store_id,
          user_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/pc/api/wechat_account.php`,
          params
        );

        if (data.data) {
          this.accountList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取公众号列表失败');
        console.error('获取公众号列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchAccountList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增公众号';
      this.isEditMode = false;
      this.accountForm = {
        id: null,
        account_name: '',
        appid: '',
        appsecret: '',
        status: 1,
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑公众号';
      this.isEditMode = true;
      this.accountForm = {
        id: row.id,
        account_name: row.account_name,
        appid: row.appid,
        appsecret: row.appsecret,
        status: row.status,
        notes: row.notes
      };
      this.dialogVisible = true;
    },

    // 提交表单（新增/编辑）
    submitAccountForm() {
      this.$refs.accountForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const { data } = await axios.post(
              `${config.BASE_URL}/pc/api/wechat_account.php`,
              {
                action,
                ...this.accountForm,
                store_id: this.userInfo.store_id,
                user_id: this.userInfo.id
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchAccountList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 删除公众号
    async handleDelete(id) {
      this.$confirm('确定要删除该公众号账户吗？删除后所有相关数据也将被删除', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/pc/api/wechat_account.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.store_id,
              user_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchAccountList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    },

    // 打开同步对话框
    openSyncDialog(row) {
      this.currentAccountId = row.id;
      this.currentAccount = row;
      this.syncDialogVisible = true;
    },

    // 处理同步
    async handleSync() {
      if (this.syncOptions.length === 0) {
        this.$message.warning('请选择要同步的选项');
        return;
      }

      this.syncing = true;
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/pc/api/wechat_account.php`,
          {
            action: 'sync_all',
            account_id: this.currentAccountId,
            store_id: this.userInfo.store_id,
            user_id: this.userInfo.id
          }
        );

        if (data.success) {
          this.$message.success('同步成功');
          this.syncDialogVisible = false;
          this.fetchAccountList();
        } else {
          this.$message.error(data.error || '同步失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '同步失败';
        this.$message.error(`同步失败: ${errorMsg}`);
        console.error('同步错误:', error);
      } finally {
        this.syncing = false;
      }
    },

    // 打开模板对话框
    openTemplateDialog(row) {
      this.currentAccountId = row.id;
      this.currentAccount = row;
      this.templateDialogTitle = `${row.account_name} - 模板管理`;
      this.templateDialogVisible = true;
    },

    // 打开用户对话框
    openUserDialog(row) {
      this.currentAccountId = row.id;
      this.currentAccount = row;
      this.userDialogTitle = `${row.account_name} - 用户管理`;
      this.userDialogVisible = true;
    },

    // 打开发送对话框
    openSendDialog(row) {
      this.currentAccountId = row.id;
      this.currentAccount = row;
      this.sendDialogTitle = `${row.account_name} - 消息发送`;
      this.sendDialogVisible = true;

      // 清空之前的选择
      this.$nextTick(() => {
        const sendComponent = this.$refs.sendMessageComponent;
        if (sendComponent) {
          sendComponent.restartProcess();
        }
      });
    },

    // 处理从模板对话框选择的模板 - 实际功能实现
    handleTemplateSelectedFromDialog(template) {
      this.$message.success(`已选择模板: ${template.title}`);

      // 自动打开发送对话框并传递模板
      this.openSendDialog(this.currentAccount);

      // 通过ref传递模板到发送组件
      this.$nextTick(() => {
        const sendComponent = this.$refs.sendMessageComponent;
        if (sendComponent) {
          sendComponent.handleTemplateSelected(template);
        }
      });
    },

    // 处理从用户对话框选择的用户 - 实际功能实现
    handleUserSelectedFromDialog(user) {
      this.$message.success(`已选择用户: ${user.remark || user.openid}`);

      // 自动打开发送对话框
      this.openSendDialog(this.currentAccount);

      // 通过ref传递用户到发送组件
      this.$nextTick(() => {
        const sendComponent = this.$refs.sendMessageComponent;
        if (sendComponent) {
          sendComponent.handleUserSelected(user);
        }
      });
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 0px;
}

.stats-info {
  font-size: 12px;
  color: #666;

  div {
    margin-bottom: 2px;
  }
}

.settings-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 操作按钮样式优化 */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.action-btn {
  margin: 2px;
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}
</style>